import { Link } from 'react-router-dom';
import { SEOPageLayout } from '@/components/seo/SEOPageLayout';

const faq = [
  { q: 'What do you say to someone who is dying?', a: 'Be present. Acknowledge the situation honestly. Tell them what they mean to you, share a memory, express forgiveness if needed, and let them know it\'s okay. Simple, honest words matter more than eloquence.' },
  { q: 'Is it okay to write instead of speaking in person?', a: 'Yes. Written messages can be powerful, especially when emotions make it hard to speak. A written message also becomes a keepsake for the family.' },
  { q: 'What if I can\'t be there in person?', a: 'A heartfelt digital message through Say It NOW can arrive instantly. It can include photos, voice recordings, and words that the person — and their family — can revisit.' },
  { q: 'How do I say goodbye without making it about me?', a: 'Focus on them: their impact, their qualities, the specific moments that mattered. Let your words be a mirror that shows them how they lived well.' },
];

const related = [
  { label: 'Digital Legacy Message', href: '/digital-legacy-message' },
  { label: 'Living Tribute Ideas', href: '/living-tribute-ideas' },
  { label: 'Heartfelt Message to Mom', href: '/heartfelt-message-to-mom' },
  { label: 'Heartfelt Message to Dad', href: '/heartfelt-message-to-dad' },
];

export default function WhatToSayBeforeSomeoneDies() {
  return (
    <SEOPageLayout
      title="What to Say Before Someone Dies — Words That Matter"
      metaDescription="Finding the right words when someone is dying is one of life's hardest moments. Compassionate guidance, real examples, and a tool to say what matters — now."
      h1="What to Say Before Someone Dies — Finding Words When It Matters Most"
      subtitle="This is one of the hardest things you'll ever do. We're here to help you through it."
      faq={faq}
      relatedPages={related}
    >
      <h2>Why This Matters More Than Anything</h2>
      <p>
        When someone you love is nearing the end of their life, the weight of unspoken words becomes unbearable. You want to say everything — but the enormity of the moment can leave you speechless. This guide is written with deep compassion for that exact situation. There are no perfect words, but there are <strong>honest ones</strong>, and those are enough.
      </p>

      <h2>What People Wish They Had Said</h2>
      <p>Hospice workers, grief counselors, and researchers have identified common themes in what people wish they'd expressed:</p>
      <ul>
        <li><strong>"Thank you"</strong> — for everything, for specific things, for their presence in your life.</li>
        <li><strong>"I forgive you"</strong> — for old wounds that no longer need to be carried.</li>
        <li><strong>"Please forgive me"</strong> — for your own regrets and shortcomings.</li>
        <li><strong>"I love you"</strong> — the simplest and most powerful words in any language.</li>
        <li><strong>"It's okay to go"</strong> — permission to let go can be the greatest gift.</li>
      </ul>

      <h2>Example Messages for End-of-Life Moments</h2>

      <h3>For a Parent</h3>
      <blockquote>
        "Dad, I want you to know that you gave me everything I needed to live a good life. Your lessons are in every decision I make, your love is in every good thing I do. I'm going to be okay — because of you. Thank you. I love you."
      </blockquote>

      <h3>For a Spouse</h3>
      <blockquote>
        "My love, our years together have been the greatest chapter of my life. I wouldn't change a single day. Thank you for choosing me, for loving me, for making ordinary life feel extraordinary. You are my everything."
      </blockquote>

      <h3>For a Friend</h3>
      <blockquote>
        "You probably don't know this, but there was a time in my life when your friendship was the only thing keeping me going. You showed up when no one else did. I need you to know that you mattered — deeply, permanently."
      </blockquote>

      <h2>When You Can't Be There</h2>
      <p>
        Sometimes distance, circumstances, or the person's condition prevent an in-person conversation. That's where a written message becomes invaluable. <Link to="/signup">Say It NOW</Link> was created for exactly this — to give you a way to <strong>say what you feel before it's too late</strong>, even when you can't be in the room.
      </p>
      <p>
        Your message is delivered privately and beautifully. It becomes something the family can keep — a <strong>digital legacy message</strong> that preserves your words forever.
      </p>

      <h2>It's Not About Being Perfect</h2>
      <p>
        There is no script for goodbye. The most meaningful words in these moments are rarely eloquent — they're raw, honest, and real. What matters is that you <em>said them</em>. That you didn't let fear or awkwardness or time steal the chance.
      </p>
      <p>
        <Link to="/signup">Say It NOW</Link> exists because we believe no one should leave this world without hearing how much they meant to someone. <strong>Say it now. Not later.</strong>
      </p>
    </SEOPageLayout>
  );
}
